/**
 * Global keyboard shortcut handler and keyboard help overlay.
 *
 * Registered once in app.ts mountApp(). Handles shortcuts that work
 * across all views (Ctrl+K, Esc, ?).
 */

/** Stored listener references for teardown. */
let _helpListener: ((e: KeyboardEvent) => void) | undefined
let _escapeListener: ((e: KeyboardEvent) => void) | undefined
/** Element that had focus before the help overlay opened, for restoration. */
let _helpTrigger: Element | null = null

/** Initialize global keyboard shortcuts. Call once on app mount. */
export function initKeyboardShortcuts(showHelp: (() => void) | null = null): void {
  _helpListener = (e: KeyboardEvent) => {
    // Skip when typing in inputs
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return
    }
    if (e.key === '?') {
      if (showHelp) showHelp()
      else showKbdHelp()
    }
  }
  document.addEventListener('keydown', _helpListener)

  _escapeListener = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    const helpOverlay = document.getElementById('kbd-help-overlay')
    if (helpOverlay && helpOverlay.style.display !== 'none') {
      closeKbdHelp()
      return
    }
    const w = window as unknown as Record<string, (() => void) | undefined>
    w.closeDrawer?.()
    w.closeDetailModal?.()
  }
  document.addEventListener('keydown', _escapeListener)
}

/** Tear down keyboard shortcuts. Call before unmounting or re-initializing. */
export function destroyKeyboardShortcuts(): void {
  if (_helpListener) {
    document.removeEventListener('keydown', _helpListener)
    _helpListener = undefined
  }
  if (_escapeListener) {
    document.removeEventListener('keydown', _escapeListener)
    _escapeListener = undefined
  }
}

/** Show keyboard shortcuts help overlay. */
function showKbdHelp(): void {
  const existing = document.getElementById('kbd-help-overlay')
  if (existing) {
    existing.style.display = 'flex'
    return
  }

  // Save the element that triggered the dialog for focus restoration
  _helpTrigger = document.activeElement

  const help = document.createElement('div')
  help.id = 'kbd-help-overlay'
  help.setAttribute('role', 'dialog')
  help.setAttribute('aria-modal', 'true')
  help.setAttribute('aria-label', 'Keyboard Shortcuts')
  help.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.7)'
  const panel = document.createElement('div')
  panel.style.cssText =
    'background:var(--s2);padding:2rem;border-radius:8px;max-width:400px;text-align:center;border:1px solid var(--bd)'

  const title = document.createElement('h2')
  title.style.cssText = 'font-size:16px;margin-bottom:1rem'
  title.textContent = 'Keyboard Shortcuts'
  panel.appendChild(title)

  const table = document.createElement('table')
  table.style.cssText = 'text-align:left;font-size:13px'
  table.appendChild(shortcutRow('Ctrl+K', 'Command palette'))
  table.appendChild(shortcutRow('Esc', 'Close modal/drawer'))
  table.appendChild(shortcutRow('1-5', 'Switch tabs'))
  panel.appendChild(table)

  const close = document.createElement('button')
  close.id = 'kbd-help-close'
  close.style.cssText =
    'margin-top:1rem;padding:6px 16px;border-radius:6px;background:var(--s3);border:1px solid var(--bd);color:var(--t1);cursor:pointer;font-family:var(--ui)'
  close.textContent = 'Close'
  panel.appendChild(close)
  help.appendChild(panel)

  // Basic focus trap: Tab cycles between close button and backdrop
  help.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      const closeBtn = help.querySelector<HTMLButtonElement>('#kbd-help-close')
      if (e.shiftKey && document.activeElement === closeBtn) {
        e.preventDefault()
        closeBtn?.focus()
      } else if (!e.shiftKey && document.activeElement === closeBtn) {
        e.preventDefault()
        closeBtn?.focus()
      }
    }
  })

  help.addEventListener('click', (e) => {
    if (e.target === help) closeKbdHelp()
  })
  document.body.appendChild(help)

  const closeBtn = help.querySelector<HTMLButtonElement>('#kbd-help-close')
  closeBtn?.addEventListener('click', () => {
    closeKbdHelp()
  })

  // Move focus into the dialog
  closeBtn?.focus()
}

function shortcutRow(keys: string, description: string): HTMLTableRowElement {
  const row = document.createElement('tr')
  const keyCell = document.createElement('td')
  keyCell.style.cssText = 'padding:0.25rem'
  const key = document.createElement('kbd')
  key.style.cssText =
    'font-family:var(--mono);font-size:11px;padding:2px 6px;background:var(--s4);border-radius:3px'
  key.textContent = keys
  keyCell.appendChild(key)
  const descCell = document.createElement('td')
  descCell.textContent = description
  row.append(keyCell, descCell)
  return row
}

/** Close the keyboard help overlay and restore focus. */
function closeKbdHelp(): void {
  const help = document.getElementById('kbd-help-overlay')
  help?.remove()
  // Restore focus to the element that triggered the dialog
  if (_helpTrigger instanceof HTMLElement) {
    _helpTrigger.focus()
  }
  _helpTrigger = null
}

// Expose for action-delegation in keyboard help overlay
;(window as unknown as Record<string, unknown>).showKbdHelp = showKbdHelp
